import React from 'react';
import { TFunction } from 'i18next';
import { MatGroup, MatItem, emptyGroup, nextGroupPriority } from '../components/OSMaterialnoTable';
import { RazmerRow } from '../components/OSRazmerTable';
import { SubprocessItemRow } from '../components/OSSubprocessItemsTable';
import { DocumentType } from '../types/Types';
import { formatQtyForDisplay, parseDecimalInput } from '../utils/decimalInput';
import { receiptEntryRole } from '../utils/workOrderRole';
import { RNMaterialnoActualRow, RNWorkOrderDoc } from './useRNWorkOrder';
import type { SubprocessOption } from '../select/SubprocessCells';

interface Args {
  activeStage: string;
  isRazmerStage: boolean;
  isCreating: boolean;
  rnWorkOrder: RNWorkOrderDoc | null;
  activeSubprocess?: SubprocessOption;
  razmerRows: RazmerRow[];
  subprocessItemRows: SubprocessItemRow[];
  interProductIssueSubprocessIds: string[];
  documentTypes: DocumentType[];
  materialnoGroups: MatGroup[];
  materialnoActualRowsRef: React.MutableRefObject<RNMaterialnoActualRow[]>;
  mergeMaterialnoActuals: (groups: MatGroup[], actuals: RNMaterialnoActualRow[]) => MatGroup[];
  setMaterialnoGroups: React.Dispatch<React.SetStateAction<MatGroup[]>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  showToast: (message: string, type: 'info') => void;
  t: TFunction;
}

// Reconcile the consuming stage's material table with receipts from its producing stage.
// Snapshot RNs still need this row: the frozen normativ contains interProducts, while
// materialno in older snapshots can contain only packaging.
export const useRNMaterialnoSeeding = ({
  activeStage,
  isRazmerStage,
  isCreating,
  rnWorkOrder,
  activeSubprocess,
  razmerRows,
  subprocessItemRows,
  interProductIssueSubprocessIds,
  documentTypes,
  materialnoGroups,
  materialnoActualRowsRef,
  mergeMaterialnoActuals,
  setMaterialnoGroups,
  setIsDirty,
  showToast,
  t,
}: Args) => {
  const materialnoGroupsRef = React.useRef(materialnoGroups);
  materialnoGroupsRef.current = materialnoGroups;

  React.useEffect(() => {
    if (!activeStage || isRazmerStage) return;
    const weighed: MatItem[] = (rnWorkOrder?.normativSnapshot ? [] : razmerRows)
      .filter((row) => row.subprocessId === activeStage && row.itemId)
      .map((row) => ({
        itemId: row.itemId,
        ident: row.ident,
        name: row.name,
        unitCode: row.unitCode,
        quantity: row.requiredQty,
        priority: row.priority,
      }));
    const receivedRows = (rnWorkOrder?.interProductReceipts || [])
      .filter((entry) => receiptEntryRole(entry, documentTypes) === 'receive-intermediate')
      .flatMap((entry) => entry.rows);
    const handedOver: MatItem[] = subprocessItemRows
      .filter((row, index) => row.rowId && row.itemId && interProductIssueSubprocessIds[index] === activeStage)
      .flatMap((row) => {
        const receiptRow = receivedRows.find((receipt) => receipt.rowKey === row.rowId);
        if (!receiptRow) return [];
        const received = Number(receiptRow.quantity) || 0;
        const sampled = parseDecimalInput(row.uzurkuvanje || '') ?? 0;
        const quantity = formatQtyForDisplay(Math.max(0, received - sampled), { showZero: true });
        return [{
          osRowId: row.rowId,
          itemId: row.itemId,
          ident: row.ident,
          name: row.name,
          unitCode: row.unitCode,
          accountingTypeCode: row.accountingTypeCode,
          accountingTypeGroup: row.accountingTypeGroup,
          quantity,
          measuredQty: quantity,
          serialNumber: receiptRow.serial || '',
          expiryDate: receiptRow.deadline || '',
          priority: '',
          receiptDerived: true,
        }];
      });
    const savedRows = mergeMaterialnoActuals(
      [{ ...emptyGroup(0, '1'), subprocessId: activeStage, items: handedOver }],
      materialnoActualRowsRef.current
    )[0].items;
    const sources = [...weighed, ...savedRows];
    if (sources.length === 0) return;
    const handedOverByRowId = new Map(handedOver.map((row) => [row.osRowId, row]));
    const handedOverByItemId = new Map(handedOver.map((row) => [row.itemId, row]));
    const previous = materialnoGroupsRef.current;
    const present = new Set<string>();
    previous.forEach((group) => {
      if (group.subprocessId === activeStage) {
        group.items.forEach((item) => { if (item.itemId) present.add(item.itemId); });
      }
    });
    const missing = sources.filter((source) => {
      if (present.has(source.itemId)) return false;
      present.add(source.itemId);
      return true;
    });
    const staleReceiptQuantities = previous.some((group) => group.subprocessId === activeStage
      && group.items.some((item) => {
        const source = handedOverByRowId.get(item.osRowId) || handedOverByItemId.get(item.itemId);
        return source && (item.osRowId !== source.osRowId
          || item.quantity !== source.quantity
          || Math.abs((parseDecimalInput(item.measuredQty || '') ?? 0)
            - (parseDecimalInput(source.quantity) ?? 0)) > 0.000001);
      }));
    const hasUnmarkedReceiptRows = previous.some((group) => group.subprocessId === activeStage
      && group.items.some((item) => {
        const source = handedOverByRowId.get(item.osRowId) || handedOverByItemId.get(item.itemId);
        return !!(source && !item.receiptDerived);
      }));
    if (missing.length === 0 && !staleReceiptQuantities && !hasUnmarkedReceiptRows) return;
    if (staleReceiptQuantities) setIsDirty(true);

    const resetConfirmedReceiptItems: MatItem[] = [];
    const updateReceiptQuantity = (items: MatItem[]) => items.map((item) => {
      const source = handedOverByRowId.get(item.osRowId) || handedOverByItemId.get(item.itemId);
      if (!source) return item;
      if (item.osRowId === source.osRowId && item.quantity === source.quantity
        && Math.abs((parseDecimalInput(item.measuredQty || '') ?? 0)
          - (parseDecimalInput(source.quantity) ?? 0)) <= 0.000001) {
        return item.receiptDerived ? item : { ...item, receiptDerived: true };
      }
      if (item.confirmed) resetConfirmedReceiptItems.push(item);
      return {
        ...item,
        osRowId: source.osRowId,
        accountingTypeCode: source.accountingTypeCode,
        accountingTypeGroup: source.accountingTypeGroup,
        quantity: source.quantity,
        measuredQty: source.quantity,
        serialNumber: source.serialNumber,
        expiryDate: source.expiryDate,
        confirmed: false,
        verifiedBy: undefined,
        verifiedByName: '',
        verifiedAt: '',
        verifyRequested: false,
        receiptDerived: true,
      };
    });
    const firstIndex = previous.findIndex((group) => group.subprocessId === activeStage);
    const nextGroups = firstIndex < 0
      ? [...previous, { ...emptyGroup(0, nextGroupPriority(previous)), subprocessId: activeStage, items: updateReceiptQuantity(missing) }]
      : previous.map((group, index) => group.subprocessId === activeStage
        ? {
          ...group,
          items: updateReceiptQuantity([
            ...group.items.filter((item) => item.itemId || item.quantity),
            ...(index === firstIndex ? missing : []),
          ]),
        }
        : group);
    materialnoGroupsRef.current = nextGroups;
    setMaterialnoGroups(nextGroups);
    resetConfirmedReceiptItems.forEach((item) => {
      const source = handedOverByRowId.get(item.osRowId) || handedOverByItemId.get(item.itemId);
      showToast(t('os.materialno.receiptQuantityRecalculated', {
        ident: item.ident,
        qty: source?.quantity || '',
        unit: item.unitCode,
      }), 'info');
    });
  }, [
    activeStage, isRazmerStage, rnWorkOrder?.normativSnapshot, rnWorkOrder?.interProductReceipts,
    razmerRows, subprocessItemRows, interProductIssueSubprocessIds, documentTypes,
    materialnoActualRowsRef, mergeMaterialnoActuals, setMaterialnoGroups, setIsDirty, showToast, t,
  ]);

  // Match /os while a new RN is still only a local preview. The POST freezes these
  // defaults in its normativ snapshot, so saved (including legacy) RNs must never
  // receive live subprocess-codebook instruction changes.
  React.useEffect(() => {
    if (!isCreating || rnWorkOrder?._id || rnWorkOrder?.normativSnapshot) return;
    if (!activeStage || isRazmerStage) return;
    const instructions = (activeSubprocess?.instructions || [])
      .map((entry) => (entry.text || '').trim())
      .filter(Boolean);
    if (instructions.length === 0) return;

    const previous = materialnoGroupsRef.current;
    const stageGroups = previous.filter((group) => group.subprocessId === activeStage);
    const present = new Set(stageGroups.map((group) => (group.instruction || '').trim()));
    const missing = instructions.filter((text, index) => !present.has(text) && instructions.indexOf(text) === index);
    if (missing.length === 0) return;

    // Wait for async material loading/seeding so instructions fill its material groups
    // in order instead of becoming rows ahead of them.
    const hasMaterialGroup = stageGroups.some((group) => !group.manual && group.items.length > 0);
    const stageHasWeighing = razmerRows.some((row) => row.subprocessId === activeStage && row.itemId);
    if (!hasMaterialGroup && stageHasWeighing) return;

    const queue = [...missing];
    const next = previous.map((group) => {
      if (queue.length === 0 || group.subprocessId !== activeStage || group.manual || group.items.length === 0) return group;
      if ((group.instruction || '').trim()) return group;
      return { ...group, instruction: queue.shift() as string };
    });

    const priorities = [
      ...stageGroups.map((group) => Number(group.priority)),
      ...razmerRows.filter((row) => row.subprocessId === activeStage).map((row) => Number(row.priority)),
    ].filter((priority) => Number.isFinite(priority));
    let nextPriority = Math.floor(priorities.length ? Math.max(...priorities) : 0) + 1;
    const extraRows: MatGroup[] = queue.map((instruction) => ({
      ...emptyGroup(0, String(nextPriority++), true),
      subprocessId: activeStage,
      instruction,
    }));

    const nextGroups = [...next, ...extraRows];
    materialnoGroupsRef.current = nextGroups;
    setMaterialnoGroups(nextGroups);
    setIsDirty(true);
  }, [
    isCreating, rnWorkOrder?._id, rnWorkOrder?.normativSnapshot, activeStage, isRazmerStage,
    activeSubprocess, razmerRows, materialnoGroups, setMaterialnoGroups, setIsDirty,
  ]);
};
